// Centralised section access for the frontend.
//
// The backend section master (/auth/sections) is the source of truth for labels,
// routes and what each section includes; the user's resolved `effective_sections`
// comes from /auth/me. Every sidebar item, page guard and edit check reads from here —
// nothing else in the UI hard-codes per-section permissions.
import { useEffect, useState } from 'react';
import api from './api';
import { useAuth } from './auth';

export const FULL_ACCESS_ROLES = ['admin', 'management'];

let _catalogue = null;
let _catalogueReq = null;

export async function loadSectionCatalogue() {
  if (_catalogue) return _catalogue;
  if (!_catalogueReq) {
    _catalogueReq = api.get('/auth/sections').then((r) => { _catalogue = r.data || []; return _catalogue; }).catch(() => { _catalogueReq = null; return []; });
  }
  return _catalogueReq;
}

export function isFullAccess(user) {
  return !!user && FULL_ACCESS_ROLES.includes(user.role);
}

export function userSections(user) {
  if (!user) return [];
  if (Array.isArray(user.effective_sections)) return user.effective_sections;
  return Array.isArray(user.sections) ? user.sections : [];
}

/** True when the user holds ANY of the given section keys (admin / management always). */
export function hasSection(user, ...keys) {
  if (!user) return false;
  if (isFullAccess(user) || user.all_sections) return true;
  const mine = userSections(user);
  return keys.some((k) => mine.includes(k));
}

/** Edit rights: admin always, management read-only, users need the section. */
export function canEditSection(user, ...keys) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (isFullAccess(user)) return false;
  return hasSection(user, ...keys);
}

/** Which section keys own a frontend path (from the catalogue). */
export function sectionsForRoute(catalogue, path) {
  return (catalogue || []).filter((s) => (s.routes || []).includes(path)).map((s) => s.key);
}

export function useAccess() {
  const { user } = useAuth();
  const [catalogue, setCatalogue] = useState(_catalogue || []);
  useEffect(() => {
    let active = true;
    if (user) loadSectionCatalogue().then((c) => { if (active) setCatalogue(c); });
    return () => { active = false; };
  }, [user]);
  return {
    user,
    catalogue,
    isAdmin: user?.role === 'admin',
    isFullAccess: isFullAccess(user),
    sections: userSections(user),
    can: (...keys) => hasSection(user, ...keys),
    canEdit: (...keys) => canEditSection(user, ...keys),
    canRoute: (path) => {
      const owners = sectionsForRoute(catalogue, path);
      if (owners.length === 0) return true;          // not a section page (dashboard, uploads, actions…)
      return hasSection(user, ...owners);
    },
    routeOwners: (path) => sectionsForRoute(catalogue, path),
  };
}
